import { useEffect, useState } from "react";
import { get } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import { ORDER_STATUS_OPTIONS } from "../FilterBar";
import {
  AttendantType,
  DetailGroupType,
  EventType,
  PagingType,
} from "routes/group/types";
import { groupApi } from "routes/group/api/group.service";
import { ClipboardIcon, DownloadIcon } from "@heroicons/react/outline";
import { formatNumber, isHaveActionPermission } from "utills/helpers";
import { useAttendantExportExcel } from "routes/group/hooks/useAttendantExportExcel";
import useQueryParams from "routes/group/hooks/useQueryParams";
import { CollapsibleSection } from "components/collapsibleSection";
import { Label } from "components/label";
import { Tag } from "components/tag";
import Button from "components/button";
import { copyToClipboard } from "routes/group/utils/helper";
import AttendantDataTable, { FilterType } from "../AttendantDataTable";
import VatDetailForm from "../VatDetailForm/VatDetailForm";

interface PropType {
  data: DetailGroupType;
  events: EventType[];
  attendants: AttendantType[];
  attendantPaging: PagingType | null;
  handleChangeAttendantPage: (page: number) => void;
  attendantFilter: FilterType;
  handleChangeAttendantFilter: (key: string, value: string) => void;
  handleSearchAttendant: (data: FilterType) => Promise<void>;
}

const DetailForm = (props: PropType) => {
  const {
    data,
    events,
    attendants,
    attendantPaging,
    handleChangeAttendantPage,
    attendantFilter,
    handleChangeAttendantFilter,
    handleSearchAttendant,
  } = props;

  const { loading: exportLoading, fetchData: exportFetchData } =
    useAttendantExportExcel();

  const { searchParams, setQueryParams } = useQueryParams();

  const infoSectionParam = searchParams.get("open_info_section") ?? "true";
  const attendantSectionParam =
    searchParams.get("open_attendant_section") ?? "true";
  const vatSectionParam = searchParams.get("open_vat_section") ?? "true";

  const handleGetSectionStatus = (value: string) => {
    if (value === "true") return true;
    if (value === "false") return false;
  };

  const formatBoolean = (value: boolean) => {
    if (value) return "true";
    else return "false";
  };

  const [infoSectionOpen, setInfoSectionOpen] = useState<boolean>(
    handleGetSectionStatus(infoSectionParam) ?? true,
  );
  const [attendantsSectionOpen, setAttendantsSectionOpen] = useState<boolean>(
    handleGetSectionStatus(attendantSectionParam) ?? true,
  );
  const [vatSectionOpen, setVatSectionOpen] = useState<boolean>(
    handleGetSectionStatus(vatSectionParam) ?? true,
  );

  const [exportId, setExportId] = useState<string | null>();

  const handleFormatStatus = (value: string) => {
    return (
      ORDER_STATUS_OPTIONS.find((status) => status.value === value) ?? null
    );
  };

  const handleGetEventInfomation = (event_id: string, events: EventType[]) => {
    if (!events) return null;

    return events?.find((event) => event?.id === event_id) ?? null;
  };

  const handleformatDob = (value: string) => {
    const m = moment(value, "YYYY-MM-DD", true); // strict
    return m.isValid() ? m.format("DD-MM-YYYY") : "";
  };

  const handleformatDate = (value: string) => {
    const m = moment(value, "YYYY-MM-DD HH:mm:ss", true); // strict
    return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : "";
  };

  const handleExportExcel = async () => {
    const res = await exportFetchData({
      // event_id: data?.event_id,
      group_id: data?.id,
      q: attendantFilter.q,
      payment_status: attendantFilter.payment_status,
    });

    if (res.is_success) setExportId(res.export_id);
  };

  // Handle Export Excel
  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        groupApi.getExportResult(exportId).then((data) => {
          const isSuccess = get(data, "data.data.is_success", false);
          if (isSuccess) {
            setExportId(null);
            window.location.href = get(data, "data.data.file_link", "#");
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportId, groupApi.getExportResult]);

  return (
    <div className="wrapper">
      <CollapsibleSection
        customOpen={{
          open: infoSectionOpen,
          onClick: () => {
            setInfoSectionOpen(!infoSectionOpen);
            setQueryParams({
              open_info_section: formatBoolean(!infoSectionOpen),
            });
          },
        }}
        label={"Group Information"}
      >
        <div className="p-6 flex flex-col gap-5">
          {/* General Information Section */}
          <h3 className="col-span-full text-[0.9rem] font-bold text-gray-600">
            General Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
            <div className="flex flex-col gap-1">
              <Label required={false} id="status" name="Registration Status" />

              <Tag
                text={
                  handleFormatStatus(get(data, "status", "").toString())
                    ?.label ?? ""
                }
                type={
                  handleFormatStatus(get(data, "status", "").toString())
                    ?.color ?? ("" as any)
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="group_name" name="Group Name" />

              <span className="text-[14px]">
                {get(data, "group_name_en", "")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="event_name" name="Event Name" />

              <span className="text-[14px]">
                {handleGetEventInfomation(get(data, "event_id", ""), events)
                  ?.name_en ?? ""}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="created_at" name="Created At" />

              <span className="text-[14px]">
                {handleformatDate(get(data, "created_at", ""))}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="expired_date" name="Expired At" />

              <span className="text-[14px]">
                {handleformatDate(get(data, "expired_date", ""))}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="num_of_mem" name="Number Of Member" />

              <span className="text-[14px]">{get(data, "num_of_mem", "")}</span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="group_token" name="Token" />

              <div className="flex items-center gap-3">
                <span className="text-[14px] font-medium text-brand-500 max-w-[200px] truncate">
                  {get(data, "group_token", "")}
                </span>

                {isHaveActionPermission("", "group_buy_management", "view") && (
                  <Button
                    className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                    onClick={() => {
                      copyToClipboard(get(data, "group_token", ""));
                    }}
                  >
                    <ClipboardIcon className="w-4 h-4 text-black" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="w-full h-[1px] bg-gray-200"></div>

          {/* Leader Information Section */}
          <h3 className="col-span-full text-[0.9rem] font-bold text-gray-600">
            Leader Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
            <div className="flex flex-col gap-1">
              <Label required={false} id="leader_name" name="Leader Name" />

              <span className="text-[14px]">
                {get(data, "leader_name", "")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="leader_email" name="Leader Email" />

              <span className="text-[14px]">
                {get(data, "leader_email", "")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="leader_phone" name="Leader Phone" />

              <span className="text-[14px]">
                (+{get(data, "leader_phone.code", "")}){" "}
                {get(data, "leader_phone.phone", "")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label
                required={false}
                id="leader_id_card"
                name="Leader ID Card"
              />

              <span className="text-[14px]">
                {get(data, "leader_id_card", "")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="leader_dob" name="Leader DOB" />

              <span className="text-[14px]">
                {handleformatDob(get(data, "leader_dob", ""))}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label required={false} id="customer_code" name="Customer Code" />

              <Link
                to={`/customer-accounts/detail/${get(
                  data,
                  "customer_code",
                  0,
                )}`}
                className="text-[14px] font-medium text-brand-500 hover:underline break-words"
              >
                {get(data, "customer_code", "")}
              </Link>
            </div>
          </div>

          <div className="w-full h-[1px] bg-gray-200"></div>

          {/* Summary Group Data Section */}
          <h3 className="col-span-full text-[0.9rem] font-bold text-gray-600">
            Amount Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
            <div className="flex flex-col gap-1">
              <Label
                required={false}
                id="summary_group_data.final_amount"
                name="Final Amount"
              />

              <span className="text-[14px]">
                {formatNumber(get(data, "summary_group_data.final_amount", 0))}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label
                required={false}
                id="summary_group_data.discount_amount"
                name="Discount Amount"
              />

              <span className="text-[14px]">
                {formatNumber(
                  get(data, "summary_group_data.discount_amount", 0),
                )}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label
                required={false}
                id="summary_group_data.total_amount"
                name="Total Amount"
              />

              <span className="text-[14px]">
                {formatNumber(get(data, "summary_group_data.total_amount", 0))}
              </span>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        customOpen={{
          open: attendantsSectionOpen,
          onClick: () => {
            setAttendantsSectionOpen(!attendantsSectionOpen);
            setQueryParams({
              open_attendant_section: formatBoolean(!attendantsSectionOpen),
            });
          },
        }}
        label={"Attendants"}
        toolbar={
          isHaveActionPermission("", "group_buy_management", "export") && (
            <Button
              className="w-11 h-11 sm:w-auto"
              loading={exportLoading || (exportId ? true : false)}
              onClick={(e: any) => {
                e?.stopPropagation();
                handleExportExcel();
              }}
            >
              <DownloadIcon className="w-4 h-4 text-white" />{" "}
              <span className="hidden sm:block sm:ml-2">Export Attendant</span>
            </Button>
          )
        }
      >
        <div id="group-attendant-datatable" className="p-6">
          <AttendantDataTable
            mode="detail"
            data={attendants}
            paging={attendantPaging as PagingType}
            handleChangePage={handleChangeAttendantPage}
            filter={attendantFilter}
            handleChangeFilter={handleChangeAttendantFilter}
            onSubmit={handleSearchAttendant}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        customOpen={{
          open: vatSectionOpen,
          onClick: () => {
            setVatSectionOpen(!vatSectionOpen);
            setQueryParams({
              open_vat_section: formatBoolean(!vatSectionOpen),
            });
          },
        }}
        label={"VAT Information"}
      >
        <div className="p-6">
          <VatDetailForm data={data} />
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default DetailForm;
